import {action, computed, makeObservable, observable, runInAction, toJS} from 'mobx';

import {api} from '../../ApiFacade';
import {mainState} from '../../AppMainState';
import {AppRoutes, logOverviewLink} from '../../routing/AppRoutes';
import {showCommunicationError, showNotFoundToast} from '../../helpers/alerts';
import {showLoaderAsync} from '../../helpers/loaders';
import {FlatListOrder, GraphPillDto, GraphTimeSpan, LogOverviewDto} from '../../swagger';
import Cookies from 'js-cookie';
import {
    DefaultGraphTimeSpan,
    DefaultOrderBy,
    GraphTimeSpanField,
    OrderByField,
} from '../../components/flats/FlatListForLogGenericState';
import {DashboardListState} from './DashboardListState';
import {redirectTo} from '../../routing/browserHistory';

export class DashboardState {
    projectId?: string;
    projectName: string | null | undefined;
    @observable
    dashboardData: Array<LogOverviewDto> | null | undefined;
    @observable
    graphTimeSpan: GraphTimeSpan = GraphTimeSpan.Hour;
    @observable
    orderBy: FlatListOrder = DefaultOrderBy;
    dashboardListStates: DashboardListState[] = [];

    constructor() {
        makeObservable(this);
        this.loadQueryParams();
    }

    setProject(projectId: string) {
        this.projectId = projectId;
        this.projectName = mainState.projects?.find((p) => p.projectId === projectId)?.name;
        this.loadDashboard();
    }

    loadDashboard(): Promise<any> {
        return showLoaderAsync(api().dashboards.apiDashboardProjectDashboardGet({
            projectId: this.projectId,
            orderBy: this.orderBy,
            graphTimeSpan: this.graphTimeSpan,
        }).then(res => {
            runInAction(() => {
                this.dashboardData = res.content;
                this.dashboardListStates = [];

                if (this.dashboardData) {
                    for (const l of this.dashboardData) {
                        this.dashboardListStates.push(new DashboardListState(l, this));
                    }
                }
            });
        }).catch((err) => {
            if (err.status < 500) {
                showNotFoundToast();
                redirectTo(AppRoutes.Settings);
            } else {
                showCommunicationError(err);
            }
        }));
    }

    refreshDashboard(): Promise<any> {
        return api().dashboards.apiDashboardProjectDashboardGet({
            projectId: this.projectId,
            orderBy: this.orderBy,
            graphTimeSpan: this.graphTimeSpan,
        }).then(res => {
            if (!this.dashboardData) {
                return;
            }
            runInAction(() => {
                this.dashboardData?.forEach(d => {
                    const cd = res.content?.find(x => x.logId === d.logId);

                    if (cd !== undefined) {
                        d.graph = cd.graph;
                        d.items = cd.items;
                        d.uptimeCheckDocument = cd.uptimeCheckDocument;
                    }

                    const ls = this.dashboardListStates.find(x => x.log?.logId === d.logId);
                    if (cd !== undefined && ls && ls.log !== undefined) {
                        ls.log.uptimeCheckDocument = cd.uptimeCheckDocument;
                        if (!ls.pauseListRefresh) {
                            ls.log.items = cd.items;
                        }
                    }
                });
            });
        }).catch(err => {
            showCommunicationError(err);
        });
    }


    loadLog(logId: string | null | undefined, flatId: string) {
        redirectTo(logOverviewLink(logId!, flatId));
    }

    @computed
    get graphData(): GraphPillDto[] {
        if (!this.dashboardData) {
            return [];
        }
        const graphData: GraphPillDto[] = [];
        this.dashboardData.forEach(l => {
            if (l.graph) {
                graphData.push(...toJS(l.graph));
            }
        });
        return graphData;
    }

    @computed
    get logsIds(): string[] {
        return this.dashboardData ? this.dashboardData?.map(l => l.logId!) : [];
    }

    @action
    setOrderBy(v: FlatListOrder) {
        this.orderBy = v;
        this.saveQueryParams();
        this.loadDashboard();
    }

    @action
    setGraphTimeSpan(v: GraphTimeSpan) {
        this.graphTimeSpan = v;
        this.dashboardData!.forEach(l => {
            l.graph = [];
        });
        this.saveQueryParams();
        this.loadDashboard();
    }

    private saveQueryParams() {
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.set(OrderByField, this.orderBy + '');
        searchParams.set(GraphTimeSpanField, this.graphTimeSpan + '');

        Cookies.set(OrderByField, this.orderBy + '');
        Cookies.set(GraphTimeSpanField, this.graphTimeSpan + '');

        const newRelativePathQuery = window.location.pathname + '?' + searchParams.toString() + window.location.hash;
        // eslint-disable-next-line no-restricted-globals
        history.pushState(null, '', newRelativePathQuery);
    }

    private loadQueryParams() {
        const searchParams = new URLSearchParams(window.location.search);

        this.orderBy = searchParams.get(OrderByField) as FlatListOrder ?? Cookies.get(OrderByField) ?? DefaultOrderBy;
        this.graphTimeSpan = searchParams.get(GraphTimeSpanField) as GraphTimeSpan ?? Cookies.get(GraphTimeSpanField) ?? DefaultGraphTimeSpan;
    }
}
